import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchPnl,
  REPORT_DIMENSIONS,
  type PnlRow,
  type ReportDimension,
} from '../../api/admin.js';
import { nanoToUsd, nanoToUsdCompact, formatPercent } from '../../lib/format.js';
import type { CsvColumn } from '../../lib/csv.js';
import { ReportFilters, useReportFilters } from './components/ReportFilters.js';
import { ExportCsvButton } from './components/ExportCsvButton.js';
import { MetricChart, type MetricSeries } from './components/MetricChart.js';
import { orderRowKeys } from './components/PivotTable.js';

const TEMPORAL_DIMS: ReadonlySet<string> = new Set(['day', 'week', 'month']);
const NULL_KEY = '∅';

const CSV_COLUMNS: CsvColumn<PnlRow>[] = [
  { header: 'bucket', value: (r) => r.bucket },
  { header: 'revenue_usd', value: (r) => nanoToUsd(r.revenueNano) },
  { header: 'cost_usd', value: (r) => nanoToUsd(r.costNano) },
  { header: 'margin_usd', value: (r) => nanoToUsd(r.marginNano) },
  { header: 'margin_pct', value: (r) => (r.marginPct === null ? '' : (r.marginPct * 100).toFixed(2)) },
  {
    header: 'markup_pct',
    value: (r) => {
      const cost = BigInt(r.costNano);
      return cost > 0n ? ((Number(BigInt(r.marginNano)) / Number(cost)) * 100).toFixed(2) : '';
    },
  },
  { header: 'calls', value: (r) => r.callCount },
];

export function PnlReportPage(): ReactElement {
  const filters = useReportFilters();
  const [groupBy, setGroupBy] = useState<ReportDimension>('day');
  const temporal = TEMPORAL_DIMS.has(groupBy);

  const query = useQuery({
    queryKey: ['admin', 'reports', 'pnl', { groupBy, f: filters.key }],
    queryFn: () => fetchPnl({ groupBy, filters: filters.toParams() }),
  });

  const rows = query.data?.rows ?? [];

  const totals = useMemo(() => {
    let revenue = 0n;
    let cost = 0n;
    for (const r of rows) {
      revenue += BigInt(r.revenueNano);
      cost += BigInt(r.costNano);
    }
    const margin = revenue - cost;
    // Margen % = margen / revenue (gross margin). Markup % = margen / costo
    // (lo que se configura en billing). Con markup 20% → margen 16,7%.
    const marginPct = revenue > 0n ? Number(margin) / Number(revenue) : null;
    const markupPct = cost > 0n ? Number(margin) / Number(cost) : null;
    return { revenue, cost, margin, marginPct, markupPct };
  }, [rows]);

  // Chart: cada barra = revenue, partido en costo (lo que pagamos) + margen
  // (lo que queda). Margen clampeado a >=0 para el apilado; la tabla muestra
  // el margen real (puede ser negativo).
  const chart = useMemo(() => {
    const revByKey = new Map<string, bigint>();
    for (const r of rows) revByKey.set(r.bucket ?? NULL_KEY, BigInt(r.revenueNano));
    const cats = orderRowKeys(revByKey.keys(), revByKey, temporal);
    const costVals = cats.map((k) => {
      const r = rows.find((x) => (x.bucket ?? NULL_KEY) === k);
      return r ? Number(BigInt(r.costNano)) : 0;
    });
    const marginVals = cats.map((k) => {
      const r = rows.find((x) => (x.bucket ?? NULL_KEY) === k);
      return r ? Math.max(0, Number(BigInt(r.marginNano))) : 0;
    });
    const series: MetricSeries[] = [
      { key: 'costo', values: costVals },
      { key: 'margen', values: marginVals },
    ];
    return { categories: cats, series };
  }, [rows, temporal]);

  const orderedRows = useMemo(() => {
    if (!temporal) return rows;
    return [...rows].sort((a, b) => {
      const ak = a.bucket ?? NULL_KEY;
      const bk = b.bucket ?? NULL_KEY;
      return ak < bk ? -1 : ak > bk ? 1 : 0;
    });
  }, [rows, temporal]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">P&amp;L · margen</h1>
          <p className="page-sub">
            Revenue vs costo del proveedor → margen · {groupBy} ·{' '}
            <span className="mono">
              {filters.from} → {filters.to}
            </span>
            <br />
            <span className="tt-sec">
              Margen % = margen / revenue · Markup % = margen / costo (lo que se configura en
              billing). Markup 20% ⇒ margen 16,7%.
            </span>
          </p>
        </div>
        <ExportCsvButton filename={`pnl-${groupBy}`} rows={rows} columns={CSV_COLUMNS} />
      </div>

      <div className="filterbar">
        <div className="group">
          <span className="label" style={{ margin: 0 }}>
            agrupar por
          </span>
          <div className="chip-group">
            {REPORT_DIMENSIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`chip${groupBy === d ? ' is-on' : ''}`}
                onClick={() => setGroupBy(d)}
                data-testid={`pnl-group-${d}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ReportFilters state={filters} />

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <>
          <div className="dash-kpis">
            <Kpi label="Revenue" value={nanoToUsdCompact(totals.revenue.toString())} />
            <Kpi label="Costo proveedor" value={nanoToUsdCompact(totals.cost.toString())} />
            <Kpi
              label="Margen"
              value={nanoToUsdCompact(totals.margin.toString())}
              tone={totals.margin < 0n ? 'bad' : 'good'}
            />
            <Kpi
              label="Margen % (s/revenue)"
              value={formatPercent(totals.marginPct)}
              tone={totals.margin < 0n ? 'bad' : 'good'}
            />
            <Kpi
              label="Markup % (s/costo)"
              value={formatPercent(totals.markupPct)}
              tone={totals.margin < 0n ? 'bad' : 'good'}
            />
          </div>

          <div className="pad-7">
          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">Composición del revenue (costo + margen)</h3>
            </header>
            <MetricChart
              categories={chart.categories}
              series={chart.series}
              formatValue={(n) => nanoToUsdCompact(BigInt(Math.round(n)).toString())}
            />
          </section>

          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Breakdown</h3>
            </header>
            <table className="table" data-testid="pnl-table">
              <thead>
                <tr>
                  <th>{groupBy}</th>
                  <th className="r">Revenue</th>
                  <th className="r">Costo</th>
                  <th className="r">Margen</th>
                  <th className="r">Margen %</th>
                  <th className="r">Markup %</th>
                </tr>
              </thead>
              <tbody>
                {orderedRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center' }}>
                      Sin datos en el rango / filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  orderedRows.map((r) => {
                    const marginNano = BigInt(r.marginNano);
                    const costNano = BigInt(r.costNano);
                    const negative = marginNano < 0n;
                    const markupPct = costNano > 0n ? Number(marginNano) / Number(costNano) : null;
                    return (
                      <tr key={r.bucket ?? NULL_KEY}>
                        <td className="mono tt-sec">{r.bucket ?? '—'}</td>
                        <td className="r mono">{nanoToUsdCompact(r.revenueNano)}</td>
                        <td className="r mono">{nanoToUsdCompact(r.costNano)}</td>
                        <td className="r mono">
                          {negative ? (
                            <span className="badge b-error">{nanoToUsdCompact(r.marginNano)}</span>
                          ) : (
                            nanoToUsdCompact(r.marginNano)
                          )}
                        </td>
                        <td className="r mono">{formatPercent(r.marginPct)}</td>
                        <td className="r mono">{formatPercent(markupPct)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </section>
          </div>
        </>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}): ReactElement {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value" style={tone === 'bad' ? { color: 'var(--error)' } : undefined}>
        {value}
      </span>
    </div>
  );
}
